import { FC, useState, useEffect, useContext } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ReferenceArea
} from 'recharts';
import { makeStyles } from '@material-ui/core/styles';
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { gpaResponseType, gpaScoreListResponseType } from "../common/type";
import { formatGrade, formatSemester, splitFormatPeriodGradeSemester } from "../common/util";
import { useTranslation } from 'react-i18next';
import { themeColors } from "../common/colors";

type fontSizeType = {
  xAxisLabel?: xAxisLabelFontSizeType;
  yAxisLabel?: string;
  lineLabel?: lineLabelFontSizeType;
  legend?: string;
};
type xAxisLabelFontSizeType = '1.2rem' | '0.8rem';
type lineLabelFontSizeType = {
  desktopFontSize: string;
  mobileFontSize: string;
};

type GPALineChartTypeProps = {
  data: gpaResponseType;
  chartSize: number[];
  fontSize?: fontSizeType;
  xAxisLabelInterval?: 0 | 1;
  isExportingPDF?: boolean;
};

type gpaComponentType = gpaResponseType & {
  scoreList: Array<gpaScoreListComponentType>;
};

type gpaScoreListComponentType = gpaScoreListResponseType & {
  categoryLabel?: string;
};

// Material-UIのスタイル設定
const useStyles = makeStyles({
  LineChartStyle: {
    '& > svg.recharts-surface': {
      paddingBottom: '40px',
    }
  }
});

/** @AIからの分析は対応不要。
const StyledPaperStyle = {
  offsetY: 22,
  offsetX: -10,
  bgColor: "#226DAE",
}
  */
const GPALineChart: FC<GPALineChartTypeProps> = ({
  data,
  chartSize,
  fontSize,
  xAxisLabelInterval,
  isExportingPDF,
}) => {
  const classes = useStyles();
  const { isDesktop, isMobile } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  const [gpaScoreData, setGpaScoreData] = useState<gpaComponentType>({
    scoreList: [],
    seriesNameList: [],
  });
  const { t } = useTranslation(); // i18next

  const LineChartStyle = {
    width: '100%',
    height: 'auto',
    maxWidth: `${chartSize[0]}px`,
    maxHeight: `${chartSize[1] + 50}px`,
    margin: 'auto',
  };
  const xAxisLabelFontSize = fontSize && fontSize.xAxisLabel ? fontSize.xAxisLabel : '1.2rem';
  const yAxisLabelFontSize = fontSize && fontSize.yAxisLabel ? fontSize.yAxisLabel : '1.2rem';
  const DesktopLineLabelFontSize = (): string => {
    if (fontSize && fontSize.lineLabel && fontSize.lineLabel.desktopFontSize) {
      return fontSize.lineLabel.desktopFontSize;
    } else {
      return '1.4rem';
    };
  };
  const MobileLineLabelFontSize = (): string => {
    if (fontSize && fontSize.lineLabel && fontSize.lineLabel.mobileFontSize) {
      return fontSize.lineLabel.mobileFontSize;
    } else {
      return '2.8rem';
    };
  };
  const OtherLineLabelFontSize = '1.2rem';
  const LineLabelFontSize = isDesktop ? DesktopLineLabelFontSize() : isMobile ? MobileLineLabelFontSize() : OtherLineLabelFontSize;
  const LedgendFontSize = fontSize && fontSize.legend ? fontSize.legend : 'max(1.2vw, .8rem)';
  const XAxisLabelInterval = xAxisLabelInterval ? xAxisLabelInterval : 0;
  const ActiveLineAnimation = !isExportingPDF;

  const defaultMinNumber = 2.4;
  const defaultMaxNumber = 2.8;

  // earned_period、earned_grade及びearned_semesterのデータをcategoryLabelのひとつの文字列として集約して追加する
  const transformScoreList = (scoreList: Array<any>) => {
    return scoreList.map(item => ({
      ...item,
      categoryLabel: `${item.earned_period}_${item.earned_grade}_${item.earned_semester_code}`,
    }));
  };

  useEffect(() => {
    data.scoreList = transformScoreList(data.scoreList);
    setGpaScoreData(data);
  }, []);

  const CustomXAxisTick = ({ x, y, payload, fontSize }: { x: number, y: number, payload: any, fontSize: xAxisLabelFontSizeType }) => {
    const value = payload.value;
    const [year, grade, semester] = value.split('_');
    const textAnchor = 'middle';
    const fill = '#666';
    const defaultFontSize = '1.2rem';

    // fontSizeは、xAxisLabelFontSizeTypeで宣言した値になる
    // フォントサイズによって、ラベルの高さ方向の位置を調節する
    // xAxisLabelFontSizeTypeの値を変更または追加する際、以下dy1Positon、dy2Positon、dy3Positonの式も変更する
    const dy1Positon = fontSize === defaultFontSize ? 16 : 9;
    const dy2Positon = fontSize === defaultFontSize ? 36 : 25;
    const dy3Positon = fontSize === defaultFontSize ? 56 : 42;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={dy1Positon} textAnchor={textAnchor} fill={fill} fontSize={fontSize}>
          {`${year}`}
        </text>
        <text x={0} y={0} dy={dy2Positon} textAnchor={textAnchor} fill={fill} fontSize={fontSize}>
          {formatGrade(grade, t)}
        </text>
        <text x={0} y={0} dy={dy3Positon} textAnchor={textAnchor} fill={fill} fontSize={fontSize}>
          {formatSemester(semester, t)}
        </text>
      </g>
    );
  };

  /** @グラフ上部に「自分」の平均値を表示するコンポーネント。
  const CustomTooltip  = () => {
    const isSelf = "自分";
    // const isStudent = "全学生平均";
    const isData = gpaScoreData.scoreList;
    let labelList: any = [];
    for(let i = 0; i < isData.length; i++){
      labelList.push(isData[i][isSelf]);
    }
    return (
      <Box className="text-center m-auto" sx={{ maxWidth: `calc(${LINE_CHART_SIZE[0]}px - 4vw)` }}>
        <Box className="flex justify-between">
        {labelList.map((value: string, index: number) => (
          <Box key={index} sx={{fontSize: "max(1.2vw, .8rem)", color: themeColors[0], marginLeft: "3vw"}}>{value}</Box>
        ))}
        </Box>
      </Box>
    )
  };
   */
  /** @AIからの分析は対応不要。
  const StyledPaper = styled(Paper)(({ theme }) => ({
    position: "relative",
    display: "inline-block",
    marginLeft: "20px",
    padding: theme.spacing(2),
    boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
    backgroundColor: StyledPaperStyle.bgColor,
    color: 'white',
  "&::before": {
    content: '""',
    position: "absolute",
    borderTop: "10px solid transparent",
    borderRight: `10px solid ${StyledPaperStyle.bgColor}`,
    borderBottom: "10px solid transparent",
    top: StyledPaperStyle.offsetY,
    left: StyledPaperStyle.offsetX,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    borderTop: "10px solid transparent",
    borderRight: `10px solid ${StyledPaperStyle.bgColor}`,
    borderBottom: "10px solid transparent",
    top: StyledPaperStyle.offsetY,
    left: StyledPaperStyle.offsetX,
    filter: `drop-shadow(-2px 2px 4px rgba(0,0,0,0.2))
             drop-shadow(-4px 4px 5px rgba(0,0,0,0.14))
             drop-shadow(-1px 1px 10px rgba(0,0,0,0.12))`,
    zIndex: -1,
  },
  }));
    */

  return (
    <>
      {/** <CustomTooltip /> @グラフ上部に「自分」の平均値を表示するコンポーネント。 */}
      <LineChart
        width={chartSize[0]}
        height={chartSize[1]}
        syncMethod={'value'}
        data={gpaScoreData.scoreList}
        margin={{
          top: 5,
          right: isDesktopStyle ? 30 : 10,
          left: isDesktopStyle ? 20 : 5,
          bottom: 15,
        }}
        className={classes.LineChartStyle}
        style={LineChartStyle}
      >
        <XAxis
          dataKey="categoryLabel"
          tick={<CustomXAxisTick x={0} y={0} payload={gpaScoreData.scoreList} fontSize={xAxisLabelFontSize} />}
          interval={XAxisLabelInterval}
          padding={{
            left: isDesktopStyle ? 20 : 10,
            right: isDesktopStyle ? 20 : 10,
          }}
        />
        {gpaScoreData.scoreList &&
          gpaScoreData.scoreList.length > 1 &&
          gpaScoreData.scoreList[gpaScoreData.scoreList.length - 1].categoryLabel ?
          // データがなくても項目ラベルがある場合には、Y軸を表示する
          <YAxis fontSize={yAxisLabelFontSize} domain={[0, 4]} allowDecimals={false} />
        : 
          <YAxis fontSize={yAxisLabelFontSize} />
        }
        <Tooltip
          labelFormatter={(label) => splitFormatPeriodGradeSemester(label as string, t)} 
          formatter={(value, name) => {
            return [value, t("gpa."+String(name))];
          }} />
        <Legend
          formatter={(value) => t("gpa."+String(value))}
          wrapperStyle={{
            width: "100%",
            left: "0",
            bottom: "5px",
            fontSize: LedgendFontSize
          }}
        />
        {gpaScoreData.scoreList &&
          gpaScoreData.scoreList.length > 1 &&
          gpaScoreData.scoreList[gpaScoreData.scoreList.length - 1].categoryLabel ?
          <ReferenceArea
            x1={gpaScoreData.scoreList[0]?.categoryLabel}
            x2={gpaScoreData.scoreList[gpaScoreData.scoreList.length - 1]?.categoryLabel}
            y1={defaultMinNumber}
            y2={2.8}
            strokeOpacity={0.3}
            label={`${t("gpa.standardRange")} (${defaultMinNumber}～${defaultMaxNumber})`}
          /> : <></>
        }
        <defs>
            <linearGradient
                id="gradationColor"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
            >
                <stop offset="0%" stopColor={themeColors[0]} />
                <stop offset="100%" stopColor={themeColors[1]} />
            </linearGradient>
        </defs>
        <Line
          key="myself"
          type="linear"
          dataKey="myself"
          fill={themeColors[0]}
          stroke="url(#gradationColor)"
          activeDot={{ r: 8 }} // indexが0の場合のみactiveDotを設定
          strokeWidth={1}
          connectNulls={true}// null値を繋げる
          isAnimationActive={ActiveLineAnimation}
        >
          <LabelList
            dataKey="myself"
            position="insideTop"
            fill={themeColors[0]}
            style={{ fontSize: LineLabelFontSize }}
          />
        </Line>
      </LineChart>
    </>
  );
};

export default GPALineChart;